import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import * as R from "ramda";

import { RankIcon } from "../../../components/RankIcon";
import { hasScoreWarning, ScoreBar } from "../../../components/ScoreBar";
import Sparkline from "../../../components/Sparkline";
import { TrendIcon } from "../../../components/TrendIcon";
import { useSparklineData } from "../../../data/hooks/usePerformanceAnalysis";
import { AggregatedLensPerformanceStatsDataByUser } from "../../../data/queries/performanceAnalysis";
import { formatTrendPercentage } from "../../../utils/number";
import NoBestPerformerWarning from "./NoBestPerformerWarning";
import VsNumbers from "./VsNumbers";

interface TeamMemberAnalysisTableProps {
  data: AggregatedLensPerformanceStatsDataByUser[];
  isLoading: boolean;
  error: Error | null;
  userFilter?: string;
  lensFilter?: string;
  permittedEmails?: string[] | null;
  onClick?: (value: string) => void;
}

const TeamMemberAnalysisTable = ({
  data,
  isLoading,
  error,
  userFilter = "all",
  lensFilter = "all",
  permittedEmails = null,
  onClick
}: TeamMemberAnalysisTableProps) => {
  const {
    data: sparklineData,
    isLoading: isLoadingSparklines
  } = useSparklineData({
    userFilter,
    lensFilter,
    permittedEmails,
    enabled: !isLoading && !!data && data.length > 0
  });

  const sortedData = R.pipe(
    R.map((item: AggregatedLensPerformanceStatsDataByUser) => R.assoc("rank", item.rank ?? (item.avg_score + 100000), item)),
    R.sortBy(R.prop("rank"))
  )(data);

  const onUserClick = (userEmail: string) => {
    if (onClick) {
      onClick(userEmail);
    }
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load team member performance: {error.message}
        </Alert>
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Team Leaderboard</Typography>
        <Typography variant="body2" color="text.secondary">
          No team member performance data available for the selected filters.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ mt: 3 }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">Team Leaderboard</Typography>
        <Typography variant="body2" color="text.secondary">
          Performance metrics for each team member based on selected filters
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 56, maxWidth: 56, minWidth: 56, color: "text.secondary" }}>Rank</TableCell>
              <TableCell sx={{ color: "text.secondary" }}>User</TableCell>
              <TableCell sx={{ width: 300, minWidth: 250, color: "text.secondary" }}>Performance</TableCell>
              <TableCell align="center" sx={{ width: 100, color: "text.secondary" }}>vs Team</TableCell>
              <TableCell align="center" sx={{ width: 100, color: "text.secondary" }}>vs Best</TableCell>
              <TableCell sx={{ width: 100, minWidth: 100, color: "text.secondary" }}>Trend</TableCell>
              <TableCell sx={{ width: 100, color: "text.secondary" }}>90-Day Trend</TableCell>
              <TableCell align="right" sx={{ width: 100, color: "text.secondary" }}># Scored</TableCell>
              <TableCell align="right" sx={{ width: 110, color: "text.secondary" }}># Not Scored</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((member) => {
              const memberSparklineData = sparklineData?.byUser?.find(
                d => d.user_email === member.user_email
              )?.scores || [];
              const showWarning = hasScoreWarning(member.scored_count, member.stddev_score);

              return (
                <TableRow
                  key={member.user_email}
                  hover
                  onClick={() => onUserClick(member.user_email)}
                  sx={{
                    backgroundColor: showWarning ? "#f8f0ea" : "inherit"
                  }}
                >
                  <TableCell align="center">
                    <RankIcon rank={member.rank}>
                      {member.rank <= 3 ? member.rank : ""}
                    </RankIcon>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      {member.user_email}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <ScoreBar
                      score={member.avg_score || 0}
                      showRange={true}
                      scoreRange={[0, 5]}
                      minScore={member.min_score}
                      maxScore={member.max_score}
                      overlayScores={[
                        { score: member.avg_score, label: "User Average" }
                      ]}
                      count={member.scored_count}
                      stddev={member.stddev_score}
                      showConfidenceInterval={true}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <VsNumbers
                      percentageDelta={member.pct_delta_vs_group_avg}
                      absoluteDelta={member.delta_vs_group_avg}
                    />
                  </TableCell>

                  <TableCell align="center">
                    {member.best_user_email ? (
                      <VsNumbers
                        percentageDelta={member.pct_delta_vs_best}
                        absoluteDelta={member.delta_vs_best}
                      />
                    ) : (
                      <NoBestPerformerWarning />
                    )}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {member.pct_delta_vs_prev_avg && <TrendIcon trendPercentage={member.pct_delta_vs_prev_avg} />}
                      <Typography
                        variant="body2"
                        sx={{
                          color: member.pct_delta_vs_prev_avg > 0 ? "success.main" :
                            member.pct_delta_vs_prev_avg < 0 ? "error.main" : "text.secondary"
                        }}
                      >
                        {formatTrendPercentage(member.pct_delta_vs_prev_avg)}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Sparkline
                      data={memberSparklineData}
                      isLoading={isLoadingSparklines}
                      width={120}
                      height={24}
                      color="#1976d2"
                    />
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="body2">
                      {member.scored_count.toLocaleString()}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="body2">
                      {member.not_scored_count.toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TeamMemberAnalysisTable;
